import React from 'react';
import { CodeSectionsResponse, SrComponent, SrComponentPropsBase } from '../BasicTypes';
import { applySmartCodeSectionFilter, codeSectionPartsToText } from '../CodeSections';
import { ReactComponentContainerProps } from './SrtComponent';
import { FormDataOutput } from './FormDataOutput';

export interface DiagnosticCodesProps extends SrComponentPropsBase {
  codeGroups?: string;
  calculatedCodeGroups?: string;  // Add this for the evaluated result
}

export const DiagnosticCodesComp: React.FC<ReactComponentContainerProps<DiagnosticCodesProps>> = (container) => {
  const props = container.props;

  if (props.calculatedCodeGroups === undefined) {
    return null;
  }

  // The id 'diagnostic-codes' is used as a key in the form data. When changing it, all potential consumers of this field will be affected.
  // If you decide to change it, make sure to update the documentation/description in the schema below accordingly.
  return (
    <div id={props.id} style={{ display: 'none' }}>
      <FormDataOutput id="diagnostic-codes" value={props.calculatedCodeGroups} type="text" isDiagnosticCodes={true} />
    </div>
  );
};

// Add code sections support for expression evaluation
function getCodeSections(componentsYamlArgs: DiagnosticCodesProps): CodeSectionsResponse<DiagnosticCodesProps>[] {
  if (componentsYamlArgs.codeGroups === undefined) {
    return [];
  }

  return [{
    propNameRead: 'codeGroups',              // Read from the template property
    propNameToSet: 'calculatedCodeGroups',   // Store evaluated result here
    codeSections: [{
      sectionText: componentsYamlArgs.codeGroups,
      condition: undefined,
      codeSectionSpacerString: '',
      smartFilter: undefined,
      outputSection: undefined,
    }],
    customEvaluate: (props, prefix, expressionRunner, get, set, templateContext) => {
      const rawParts = expressionRunner(prefix) ?? [];
      const langCode = templateContext.langCode;

      // Convert the evaluated parts back to a simple string
      return codeSectionPartsToText(applySmartCodeSectionFilter(rawParts, true), langCode);
    },
  }];
}

export const DiagnosticCodes: SrComponent<DiagnosticCodesProps> = {
  getCodeSections: getCodeSections,  // Add this line to enable expression evaluation
  key: 'DiagnosticCodes',
  render: (props, context, templateContext, functions) =>
    <DiagnosticCodesComp props={props} context={context} templateContext={templateContext} functions={functions} />,
  template: '',
  toolboxName: 'DiagnosticCodes',
  schema: {
    type: 'object',
    required: ['codeGroups'],
    properties: {
      codeGroups: {
        type: 'string',
        description: 'Diagnostic code groups represented by a string. Multiple code groups are separated by semi-colon (;). Codes within code groups are separated by pipe symbols (|). Supports variable expressions like {codeGroups}. The value will be included in a field called "diagnostic-codes" in the form data by default.',
      },
    },
    description: 'Component for storing diagnostic code groups that can be extracted and included in IDS7 reports.',
  },
  getInitValues: () => ({
    codeGroups: '',
    calculatedCodeGroups: '',
  })
};
